// In src/pages/NewsletterComposerPage.js

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Container, Typography, Box, Paper, TextField, Button,
  CircularProgress, Alert,
} from '@mui/material';

// For simplicity, we'll use a standard TextField for the body.
// For a true rich text (HTML) experience, you could integrate a library
// like 'react-quill' or '@lexical/react' here.

function NewsletterComposerPage() {
  // --- MODIFIED: Get the real-time churchProfile and currentUser from context ---
  const { userProfile, churchProfile, currentUser } = useAuth();
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const canSend = userProfile?.canSendNotifications ||
                  userProfile?.role === 'Church Administrator' ||
                  userProfile?.role === 'Super Administrator';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    setSuccess('');

    if (!subject.trim() || !htmlBody.trim()) {
      setError('Subject and Message Body are required.');
      setSending(false);
      return;
    }

    try {
      // Get the user's ID token for authentication
      const idToken = await currentUser.getIdToken();
      
      // Get your cloud function URL from the Firebase console
      const functionUrl = "https://us-central1-my-church-app-2.cloudfunctions.net/sendEmailBlast";

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}` // Send token for auth
        },
        body: JSON.stringify({
          // The data needs to be wrapped in a 'data' object for onCall functions
          data: {
            churchId: userProfile.churchId,
            subject,
            htmlBody,
          }
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // If the server response is not ok, throw an error with the message
        // The actual error from an onCall function is often nested in result.error.message
        throw new Error(result.error?.message || 'Something went wrong');
      }

      // The success message is nested in result.result for onCall functions
      setSuccess(result.result.message);
      setSubject('');
      setHtmlBody('');
    } catch (err) {
      console.error("Error sending email blast:", err);
      setError(`Failed to send email: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  // --- MODIFIED: Use the real-time churchProfile for this check ---
  if (!churchProfile?.enableEmailing) {
    return (
      <Container>
        <Alert severity="error">
          Email features are not enabled for your church.
        </Alert>
      </Container>
    );
  }

  if (!canSend) {
    return (
      <Container>
        <Alert severity="error">
          You do not have permission to send newsletters.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
        <Typography variant="h4" gutterBottom>Send Email Newsletter</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Compose an email to be sent to all church members who have opted-in to receive email communications.
        </Typography>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email Subject"
            name="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Message Body (HTML is supported)"
            name="htmlBody"
            multiline
            rows={15}
            value={htmlBody}
            onChange={(e) => setHtmlBody(e.target.value)}
            margin="normal"
            required
            helperText="You can use simple HTML tags like <b> for bold, <i> for italics, <p> for paragraphs, and <a> for links."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={sending}
            sx={{ mt: 2 }}
          >
            {sending ? <CircularProgress size={24} /> : `Send to All Opted-In Members`}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default NewsletterComposerPage;